import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Text,
  Heading,
  Link,
  HeadingProps,
  AbstractButton,
  IconButton,
} from 'pounceV2';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AIInferenceStreamUsage,
  AISummarizeKind,
  OutputLength,
  RiskScoreEvidence,
} from 'Generated/schema';
import { PRINT_SUPPRESS_ATTR, PRINT_ONLY_ATTR } from 'Components/utils/printAIResponse';
import MermaidDiagramV3 from 'Components/AIExplainLogEvents/AIMermaidDiagramsV3';
import { useGetGeneralSettings } from 'Source/graphql/queries/getGeneralSettings.generated';
import GenericCodeBlockV3 from 'Components/CodeBlock/GenericCodeBlockV3';
import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import { useTheme, Theme } from '@mui/material';
import { RiskScoreBadgeV3 } from 'Pages/Alerts/AlertDetails/common/RiskScoreBadge';
import { classificationToRiskTag } from 'Pages/Alerts/ListAlerts/utils';
import { isAllowedAILink } from './safeAILink';
import AITableV3 from './AITableV3';
import { useGenericAISummaryContextV3 } from '../AIExplainLogEvents/GenericAISummaryContextV3';
import PantherCodeBlockV3 from './PantherCodeBlockV3';
import PantherDeepLinkV3 from './PantherDeepLinkV3';
import { isSameOriginAsConsole, pathOf } from './consoleLinkInterceptor';
import CollapsibleBlockV3 from './CollapsibleBlockV3';
import RiskScoreScaleV3 from './RiskScoreScale/RiskScoreScaleV3';
import {
  RiskScoreData,
  convertRiskScoreEvidenceToData,
  injectRiskScoreEvidenceBlock,
} from './riskScoreEvidenceUtils';
import {
  stripUnclosedMermaidTag,
  processReasoningTags,
  normalizePantherPrompts,
  extractContentAfterAnalysis,
  wrapAnalysisBlock,
  getNextChunkForTypewriterEffect,
} from './enrichedAIResponseUtils';

export const RISK_EXPLAIN_PROMPT =
  'Explain the risk scoring process in detail and how it was used for this analysis.';

/**
 * Button component that triggers a drilldown to explain risk scoring.
 * In non-conversational mode, uses GenericAISummaryContextV3 to trigger a rerun.
 * In conversational mode, accepts an onExplain callback to send a follow-up message.
 */
const RiskExplainButton: React.FC<{ onExplain?: () => void }> = ({ onExplain }) => {
  const context = useGenericAISummaryContextV3();

  // Neither callback path available — hide the button entirely
  if (!onExplain && !context) {
    return null;
  }

  const handleClick = () => {
    if (onExplain) {
      onExplain();
    } else {
      context.rerun({
        additionalPrompt: RISK_EXPLAIN_PROMPT,
        kind: AISummarizeKind.DrillDown,
        outputLength: OutputLength.Large,
      });
    }
  };

  return (
    <Box as="span" ml={3} {...{ [PRINT_SUPPRESS_ATTR]: true }}>
      <Button
        variant="link-primary"
        title="Click to explain how classification was determined"
        onClick={handleClick}
      >
        Explain
      </Button>
    </Box>
  );
};

const headingProps: Record<number, HeadingProps> = {
  1: { variant: 'h4', fontWeight: 'bold', mt: 4, mb: 2 },
  2: { variant: 'h5', fontWeight: 'bold', mt: 4, mb: 2 },
  3: { variant: 'h6', fontWeight: 'bold', mt: 2, mb: 1 },
};

export interface MarkdownRendererOptions {
  onSearchRequest?: (query: string) => void;
  isKendraEnabled?: boolean;
  riskData?: RiskScoreData | null;
  componentTokens?: Theme['palette']['componentTokens'];
  tokens?: Theme['palette']['tokens'];
  riskScoreEvidence?: RiskScoreEvidence | null;
  onRiskExplain?: () => void;
  onPromptClick?: (prompt: string) => void;
  suppressPromptBlocks?: boolean;
}

export const getMarkdownRenderers = ({
  onSearchRequest,
  isKendraEnabled,
  riskData,
  componentTokens,
  tokens,
  riskScoreEvidence,
  onRiskExplain,
  onPromptClick,
  suppressPromptBlocks,
}: MarkdownRendererOptions) => {
  return {
    p: props => <Box my={2} color={tokens.content.text.neutral.base} {...props} />,
    h1: props => <Heading {...headingProps[1]} {...props} />,
    h2: props => {
      const { children, ...rest } = props;

      // Extract text content from children (may be string or array of elements)
      let textContent = '';
      if (typeof children === 'string') {
        textContent = children;
      } else if (Array.isArray(children)) {
        textContent = children.map(child => (typeof child === 'string' ? child : '')).join('');
      }

      // Check if this is a Risk Classification heading and we have risk data from API
      if (
        riskData &&
        (textContent === 'Risk Classification' || textContent.startsWith('Risk Classification:'))
      ) {
        const scoreColorMap = {
          Benign: tokens.content.text.semantic.success.base,
          Inconclusive: tokens.content.text.semantic.suggest.base,
          Risky: tokens.content.text.semantic.error.base,
        } as const;
        // Render the heading with classification badge, score, and RiskScoreScale visualization below
        return (
          <Box>
            <Heading {...headingProps[2]} {...rest}>
              {children}
              {/* Interactive display - suppressed during print */}
              <Box
                as="span"
                display="inline-flex"
                ml={2}
                verticalAlign="middle"
                {...{ [PRINT_SUPPRESS_ATTR]: true }}
              >
                {riskScoreEvidence && (
                  <RiskScoreBadgeV3
                    contextTags={[classificationToRiskTag(riskScoreEvidence.classification)]}
                  />
                )}
              </Box>
              <Box as="span" {...{ [PRINT_SUPPRESS_ATTR]: true }}>
                <Text as="span" ml={2} variant="body/sm" color={tokens.content.text.neutral[3]}>
                  Score:
                </Text>
                <Text
                  as="span"
                  ml={1}
                  variant="body/sm"
                  fontWeight="bold"
                  color={scoreColorMap[riskData.classification]}
                >
                  {riskData.score.toFixed(2)}
                </Text>
              </Box>
              <RiskExplainButton onExplain={onRiskExplain} />
            </Heading>
            {/* Print-friendly text - hidden on screen, shown in print */}
            <Box sx={{ display: 'none' }} {...{ [PRINT_ONLY_ATTR]: true }}>
              <Text>Classification: {riskData.classification}</Text>
              <Text>Score: {riskData.score.toFixed(2)}</Text>
            </Box>
            <RiskScoreScaleV3
              score={riskData.score}
              classification={riskData.classification}
              benignThreshold={riskData.benignThreshold}
              riskyThreshold={riskData.riskyThreshold}
            />
          </Box>
        );
      }

      // Regular h2 heading
      return <Heading {...headingProps[2]} {...props} />;
    },
    h3: props => <Heading {...headingProps[3]} {...props} />,
    a: ({ href, children, ...rest }) => {
      // Same-origin AI deep links render via React Router's <Link> for in-app
      // navigation (cmd+click still opens in a new tab via native browser
      // behavior). The Go-side deeplinks registry bounds what the AI emits;
      // a hallucinated same-origin path that has no matching route falls
      // through to React Router's 404 on click.
      if (typeof href === 'string' && isSameOriginAsConsole(href)) {
        const path = pathOf(href);
        if (path) {
          return <PantherDeepLinkV3 to={path}>{children}</PantherDeepLinkV3>;
        }
      }
      // AI output is untrusted — only render a real clickable link when the
      // URL targets a known vendor surface. See safeAILink.ts for the allowlist.
      //
      // Blocked URLs (malformed, http:, javascript:, unknown hosts, etc.) are
      // rendered as an aria-disabled anchor with NO href, so the link text
      // remains visible for context but the element cannot navigate — even via
      // keyboard activation, programmatic click, or CSS :link overrides.
      //
      // `rest` is spread first so our controlled props (size/external/href/
      // disabled) always win, even if the markdown AST were ever to include
      // an override attribute.
      if (typeof href === 'string' && isAllowedAILink(href)) {
        return (
          <Link {...rest} size="medium" external href={href}>
            {children}
          </Link>
        );
      }
      return (
        <Link {...rest} size="medium" disabled={true}>
          {children}
        </Link>
      );
    },
    ul: props => (
      <Flex
        as="ul"
        ml={6}
        direction="column"
        gap={2}
        sx={{ listStyle: 'disc', color: tokens.content.text.neutral.base }}
        {...props}
      />
    ),
    ol: props => (
      <Flex
        as="ol"
        ml={6}
        direction="column"
        gap={2}
        sx={{ listStyle: 'decimal', color: tokens.content.text.neutral.base }}
        {...props}
      />
    ),
    li: props => <Box as="li" {...props} />,
    table: ({ children }) => <AITableV3>{children}</AITableV3>,
    thead: props => <Box as="thead" {...props} />,
    tbody: props => <Box as="tbody" {...props} />,
    tr: props => <Box as="tr" {...props} />,
    th: props => (
      <Box
        as="th"
        borderColor={tokens.stroke.neutral[4]}
        borderWidth={1}
        borderStyle="solid"
        p="8px 12px"
        textAlign="left"
        fontWeight="bold"
        color={componentTokens.table.colors.text.header.resting}
        {...props}
      />
    ),
    td: props => (
      <Box
        as="td"
        borderColor={tokens.stroke.neutral[4]}
        borderWidth={1}
        borderStyle="solid"
        p="8px 12px"
        color={componentTokens.table.colors.text.row.primary}
        {...props}
      />
    ),
    code(props) {
      const { className, children } = props;

      const language = /language-(\w+)/.exec(className || '')?.[1];
      if (language && children) {
        if (language === 'mermaid') {
          return <MermaidDiagramV3 mermaidBlock={children} />;
        }
        if (language === 'panther') {
          return (
            <PantherCodeBlockV3 onPromptClick={onPromptClick} suppress={suppressPromptBlocks}>
              {children}
            </PantherCodeBlockV3>
          );
        }
        if (language === 'thinking') {
          return (
            <CollapsibleBlockV3
              value={children}
              title="Thinking steps"
              blockType="thinking"
              onSearchRequest={onSearchRequest}
            />
          );
        }
        if (language === 'analysis') {
          return (
            <CollapsibleBlockV3
              value={children}
              title="Analysis"
              blockType="analysis"
              onSearchRequest={onSearchRequest}
            />
          );
        }
        if (language === 'riskscore') {
          const titleExtra = riskData ? (
            <>
              <Flex align="center" ml={2} gap={1}>
                <Badge variant="success-subtle">{riskData.benignCount}</Badge>
                <Text variant="body/sm">Benign</Text>
              </Flex>
              <Flex align="center" ml={2} gap={1}>
                <Badge variant="error-subtle">{riskData.riskyCount}</Badge>
                <Text variant="body/sm">Risky</Text>
              </Flex>
            </>
          ) : undefined;
          return (
            <CollapsibleBlockV3
              value={children}
              title="Evidence"
              blockType="riskscore"
              onSearchRequest={onSearchRequest}
              titleExtra={titleExtra}
            />
          );
        }
        const keyIndex = `${language}-${String(children).substring(0, 50)}`;
        return (
          <GenericCodeBlockV3 key={`code-${keyIndex}`} language={language}>
            {children}
          </GenericCodeBlockV3>
        );
      }

      // inline code
      let text = '';
      if (typeof children === 'string') {
        text = children;
      } else if (Array.isArray(children)) {
        text = children.join('');
      }

      // Check if this is a classification badge
      const classificationMatch = text.match(/^classification:(Benign|Risky|Inconclusive)$/);
      if (classificationMatch) {
        const classification = classificationMatch[1] as 'Benign' | 'Risky' | 'Inconclusive';
        const variantMap = {
          Benign: 'success-outline',
          Inconclusive: 'warning-outline',
          Risky: 'error-outline',
        } as const;
        return (
          <Badge variant={variantMap[classification]} borderStyle="pill">
            {classification}
          </Badge>
        );
      }

      // Check if this is a panther citation (handles inline code in tables)
      if (text.startsWith('panther ')) {
        return <PantherCodeBlockV3 onPromptClick={onPromptClick}>{text}</PantherCodeBlockV3>;
      }

      if (isKendraEnabled && onSearchRequest) {
        return (
          <AbstractButton cursor="pointer" onClick={() => onSearchRequest(text)}>
            <Text
              as="span"
              fontFamily="monospace"
              color={componentTokens.buttons.colors.link.orange.text.resting}
              _hover={{
                textDecoration: 'underline',
                color: componentTokens.buttons.colors.link.orange.text.hover,
              }}
            >
              {' '}
              {children}{' '}
            </Text>
          </AbstractButton>
        );
      }

      return (
        <Text
          as="span"
          fontFamily="monospace"
          color={componentTokens.buttons.colors.link.orange.text.resting}
        >
          {' '}
          {children}{' '}
        </Text>
      );
    },
    pre: ({ children }) => {
      if (!children?.props?.className) {
        return <Box as="pre">{children}</Box>;
      }
      const language = /language-(\w+)/.exec(children.props.className || '')?.[1];
      if (
        language === 'mermaid' ||
        language === 'panther' ||
        language === 'thinking' ||
        language === 'analysis' ||
        language === 'riskscore'
      ) {
        return <>{children}</>;
      }
      return <Box as="pre">{children}</Box>;
    },
  };
};

export const UsageStats = ({ usage }: { usage: AIInferenceStreamUsage }) => {
  const {
    palette: { tokens },
  } = useTheme();
  // Anthropic reports input tokens in three buckets: uncached, cache-write, and cache-read.
  // The headline number reflects the full prompt size; cache activity is broken out so the
  // user can see when a request hit/wrote the prompt cache. Both counters can be non-zero
  // on the same request (e.g. a cache hit on the system prompt plus a new cache write that
  // extends the cached prefix), so list both parts independently when both are present.
  const totalInputTokens =
    usage.inputTokens + usage.cacheCreationInputTokens + usage.cacheReadInputTokens;
  const cacheParts: string[] = [];
  if (usage.cacheReadInputTokens > 0) {
    cacheParts.push(`${usage.cacheReadInputTokens.toLocaleString()} cached`);
  }
  if (usage.cacheCreationInputTokens > 0) {
    cacheParts.push(`${usage.cacheCreationInputTokens.toLocaleString()} written to cache`);
  }
  const cacheBreakdown = cacheParts.length > 0 ? ` (${cacheParts.join(', ')})` : '';
  return (
    <Text fontSize="x-small" fontStyle="italic" color={tokens.content.text.neutral[3]}>
      {usage.modelID} • {totalInputTokens.toLocaleString()} input tokens{cacheBreakdown} •{' '}
      {usage.outputTokens.toLocaleString()} output tokens • {usage.durationSeconds.toFixed(2)}s
    </Text>
  );
};

const EnrichedAIResponse = ({
  response,
  usage,
  onSearchRequest,
  finished,
  riskData,
  riskScoreEvidence,
  onRiskExplain,
  onPromptClick,
  suppressPromptBlocks,
}: {
  response: string;
  usage?: AIInferenceStreamUsage;
  onSearchRequest?: (query: string) => void;
  finished: boolean;
  riskData?: RiskScoreData | null;
  riskScoreEvidence?: RiskScoreEvidence | null;
  onRiskExplain?: () => void;
  onPromptClick?: (prompt: string) => void;
  suppressPromptBlocks?: boolean;
}) => {
  const { data } = useGetGeneralSettings();
  const isKendraEnabled = data?.generalSettings?.pantherAIKendra?.enabled;
  const copyToClipboard = useCopyToClipboardV3();
  const {
    palette: { componentTokens, tokens },
  } = useTheme();

  const renderers = React.useMemo(() => {
    return getMarkdownRenderers({
      onSearchRequest,
      isKendraEnabled,
      riskData,
      componentTokens,
      tokens,
      riskScoreEvidence,
      onRiskExplain,
      onPromptClick,
      suppressPromptBlocks,
    });
  }, [
    onSearchRequest,
    isKendraEnabled,
    riskData,
    componentTokens,
    tokens,
    riskScoreEvidence,
    onRiskExplain,
    onPromptClick,
    suppressPromptBlocks,
  ]);

  const handleCopyClick = React.useCallback(() => {
    const contentAfterAnalysis = extractContentAfterAnalysis(response);
    const normalizedContent = normalizePantherPrompts(contentAfterAnalysis);
    copyToClipboard(normalizedContent);
  }, [response, copyToClipboard]);

  if (!response) {
    return null;
  }

  return (
    <Box>
      <Markdown components={renderers} remarkPlugins={[remarkGfm]}>
        {response}
      </Markdown>
      {finished && (
        <Flex alignItems="center" gap={1}>
          <IconButton size="small" icon="copy" onClick={handleCopyClick} variant="ghost" />
          {usage && <UsageStats usage={usage} />}
        </Flex>
      )}
    </Box>
  );
};

const ProcessedEnrichedAIResponseV3 = ({
  response,
  finished,
  usage,
  onSearchRequest,
  riskScoreEvidence,
  onRiskExplain,
  onPromptClick,
  suppressPromptBlocks,
}: {
  response: string;
  finished: boolean;
  usage?: AIInferenceStreamUsage;
  onSearchRequest?: (query: string) => void;
  riskScoreEvidence?: RiskScoreEvidence | null;
  onRiskExplain?: () => void;
  onPromptClick?: (prompt: string) => void;
  suppressPromptBlocks?: boolean;
}) => {
  // Convert the API evidence to the visualization data format
  const riskData = React.useMemo(
    () => convertRiskScoreEvidenceToData(riskScoreEvidence),
    [riskScoreEvidence],
  );

  const processedResponse = React.useMemo(() => {
    // Process in order: reasoning tags -> inject evidence from API -> wrap analysis
    let processed = processReasoningTags(response);
    processed = injectRiskScoreEvidenceBlock(processed, riskScoreEvidence);
    processed = wrapAnalysisBlock(processed);
    return processed;
  }, [response, riskScoreEvidence]);

  const [displayedContent, setDisplayedContent] = React.useState(() =>
    finished ? processedResponse : '',
  );

  // Backend finished AND typewriter caught up = fully rendered
  const isFullyFinished = finished && displayedContent === processedResponse;

  // Typewriter effect: snap immediately when finished, animate word-by-word while streaming.
  // Checking `finished` directly (rather than a frozen mount-time snapshot) handles:
  // - Stop button: finished transitions false→true → snap immediately
  // - Cache-stale historical messages: Apollo cache-and-network may serve finished=false
  //   briefly; when the network responds with finished=true, we snap instead of typewriting
  //   the full historical content.
  React.useEffect(() => {
    if (finished) {
      if (displayedContent !== processedResponse) {
        setDisplayedContent(processedResponse);
      }
      return undefined;
    }

    // Streaming messages: typewriter effect
    const timeout = setTimeout(() => {
      if (displayedContent !== processedResponse) {
        const nextChunk = getNextChunkForTypewriterEffect(processedResponse, displayedContent);
        setDisplayedContent(nextChunk);
      }
    }, 40);
    return () => clearTimeout(timeout);
  }, [processedResponse, displayedContent, finished]);

  return (
    <EnrichedAIResponse
      response={stripUnclosedMermaidTag(displayedContent)}
      usage={isFullyFinished ? usage : undefined}
      onSearchRequest={onSearchRequest}
      finished={isFullyFinished}
      riskData={riskData}
      riskScoreEvidence={riskScoreEvidence}
      onRiskExplain={onRiskExplain}
      onPromptClick={onPromptClick}
      suppressPromptBlocks={suppressPromptBlocks}
    />
  );
};

export default React.memo(ProcessedEnrichedAIResponseV3);
